/**
 * js/reminders-local-store.js — the per-device completion ledger for Reminders
 * (STORAGE_KEYS.REMINDERS_COMPLETIONS, local-only, never synced). Exposes
 * window.RemindersLocalStore. EAGER (index.html, after reminders-utils.js and
 * before reminders.js) and precached (sw.js): _load() reconciles against the
 * ledger at boot.
 *
 * Moved verbatim out of js/reminders.js (plan Session 4,
 * docs/plans/reminders-mcp-plan.md). reminders.js destructures every export
 * back at eval time under its old underscore name (the reminders-utils.js
 * precedent), so every call site is unchanged. What reads and heals from the
 * ledger (_reconcileFromLedger) stays beside _save in reminders.js, with the
 * local safety snapshot: those are the data-loss guards and they belong next
 * to the write they guard.
 */
window.RemindersLocalStore = (() => {
    'use strict';

    function _normalizeLedger(ledger) {
        if (!ledger || typeof ledger !== 'object' || Array.isArray(ledger)) return {};
        const out = {};
        for (const [id, completions] of Object.entries(ledger)) {
            if (!id || !Array.isArray(completions)) continue;
            out[String(id)] = completions.filter(Boolean).map(String);
        }
        return out;
    }

    function _ledgerFromTasks(tasks) {
        const out = {};
        for (const task of Array.isArray(tasks) ? tasks : []) {
            if (!task || !task.id || !Array.isArray(task.completions)) continue;
            const list = task.completions.filter(Boolean).map(String);
            // Only seed tasks this snapshot shows as completed. Seeding an EMPTY
            // entry for a never-completed task is what broke cross-device sync:
            // _reconcileFromLedger would then clobber a completion that arrived
            // from ANOTHER device via the synced blob. Keeping the ledger free of
            // empty entries makes an empty entry mean exactly one thing —
            // "explicitly uncompleted here" (only _ledgerRecord writes those).
            if (!list.length) continue;
            out[String(task.id)] = list;
        }
        return out;
    }

    // ── Completion ledger (local-only, never synced) ──
    //
    // SupabaseSync now protects locally dirty reminder blobs from boot-time
    // restore clobbers, but this per-task ledger still covers older already-
    // clobbered state and cross-device stale rows that are newer than this
    // device's last push. Recurring tasks self-heal — _autoAdvanceRecurring
    // rolls their dueDate forward — but a 'once' task has no such healing, so
    // a lost completion shows "Past due" forever. This ledger mirrors each
    // task's completion list per device and is NOT in DOC_LOCAL_KEYS; _load()
    // re-applies it on top of the synced blob when needed.

    function _ledgerLoad() {
        try {
            const raw = localStorage.getItem(STORAGE_KEYS.REMINDERS_COMPLETIONS);
            const obj = raw ? JSON.parse(raw) : {};
            return (obj && typeof obj === 'object' && !Array.isArray(obj)) ? obj : {};
        } catch (e) {
            console.warn('[Reminders] ledger load failed:', e && e.message);
            return {};
        }
    }

    function _ledgerSave(led) {
        try {
            localStorage.setItem(STORAGE_KEYS.REMINDERS_COMPLETIONS, JSON.stringify(led));
        } catch (e) {
            console.warn('[Reminders] ledger save failed:', e && e.message);
        }
    }

    /** Record this device's authoritative completion list for a task. */
    function _ledgerRecord(task) {
        if (!task || !task.id) return;
        const led = _ledgerLoad();
        led[task.id] = Array.isArray(task.completions) ? task.completions.slice() : [];
        _ledgerSave(led);
    }


    return {
        normalizeLedger: _normalizeLedger,
        ledgerFromTasks: _ledgerFromTasks,
        ledgerLoad: _ledgerLoad,
        ledgerSave: _ledgerSave,
        ledgerRecord: _ledgerRecord,
    };
})();
